import { usePageBottomInset } from "@/hooks/use-page-bottom-inset";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { SurfaceProvider, useSurfaceContext, type SurfaceKind } from "./Surface";
import { SURFACE_BACKGROUND } from "./surface-background";
import { useElementRef } from "./use-element-ref";

/** Classes of `PageContainer`: gutter, space under the app header (`top`) and above the page end (`bottom`). */
export const pageContainerVariants = cva("", {
  variants: {
    gutter: {
      container: "mx-auto w-container",
      inset: "px-4",
      none: "",
    },
    top: {
      none: "",
      gap: "pt-4",
      header: "pt-header",
      "header-gap": "pt-header-gap",
      "header-section": "pt-header-section",
    },
    bottom: {
      none: "",
      gap: "pb-4",
      chrome: "",
    },
  },
  defaultVariants: { gutter: "container", top: "none", bottom: "none" },
});

/**
 * Props of `PageContainer`; `bottom="chrome"` pads by the measured fixed footer plus the floating gap.
 * `surface` defaults to `page` unless an enclosing surface (card, sheet) was already declared; `paint` fills its background.
 */
export type PageContainerProps = VariantProps<typeof pageContainerVariants> &
  HTMLAttributes<HTMLElement> & {
    as?: "div" | "section" | "main" | "header" | "article";
    surface?: SurfaceKind;
    /** Paints the resolved surface's background (instead of a raw `bg-card` / `bg-page` class). */
    paint?: boolean;
    /** Least bottom padding in px that `bottom="chrome"` keeps when no fixed chrome is mounted. */
    bottomMin?: number;
  };

/** Page column with the storefront gutter (90% centered by default); text inside reads as page text. */
export const PageContainer = forwardRef<HTMLElement, PageContainerProps>(function PageContainer(
  { as: Tag = "div", gutter, top, bottom, bottomMin = 0, surface, paint = false, className, style, children, ...rest },
  ref,
) {
  const elementRef = useElementRef(ref);
  const enclosing = useSurfaceContext();
  const bottomInset = usePageBottomInset();
  const resolvedSurface = surface ?? enclosing ?? "page";
  const resolvedStyle =
    bottom === "chrome" ? { paddingBottom: Math.max(bottomInset, bottomMin), ...style } : style;
  return (
    <Tag
      {...rest}
      ref={elementRef}
      style={resolvedStyle}
      className={cn(pageContainerVariants({ gutter, top, bottom }), paint && SURFACE_BACKGROUND[resolvedSurface], className)}
    >
      <SurfaceProvider surface={resolvedSurface}>{children}</SurfaceProvider>
    </Tag>
  );
});

/** Classes of `Stack`: flex layout on the 4px gap scale. */
export const stackVariants = cva("flex", {
  variants: {
    direction: { col: "flex-col", row: "flex-row" },
    gap: {
      0: "gap-0",
      0.5: "gap-0.5",
      1: "gap-1",
      1.5: "gap-1.5",
      2: "gap-2",
      3: "gap-3",
      4: "gap-4",
      5: "gap-5",
      6: "gap-6",
      8: "gap-8",
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch",
      baseline: "items-baseline",
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
    },
    wrap: { true: "flex-wrap", false: "" },
  },
  defaultVariants: { direction: "col", gap: 0, wrap: false },
});

/** Props of `Stack`. */
export type StackProps = VariantProps<typeof stackVariants> &
  HTMLAttributes<HTMLElement> & {
    as?: "div" | "section" | "article" | "ul" | "ol" | "li" | "nav" | "header" | "footer" | "aside" | "main" | "form";
  };

/** Flex column or row with token gaps. */
export const Stack = forwardRef<HTMLElement, StackProps>(function Stack(
  { as: Tag = "div", direction, gap, align, justify, wrap, className, ...rest },
  ref,
) {
  const elementRef = useElementRef(ref);
  return (
    <Tag
      {...rest}
      ref={elementRef}
      className={cn(stackVariants({ direction, gap, align, justify, wrap }), className)}
    />
  );
});
